/**
 * Rent It Forward — V2 design tokens (single source of truth).
 *
 * This file is the canonical home for every brand colour, radius, font stack, and
 * structural size used by the marketing chrome and the in-app surfaces that share
 * its theming (auth screens, payment summary, booking detail panels, etc).
 *
 * Two consumers:
 *   1. `tailwind.config.ts` imports these tokens and exposes them as Tailwind
 *      utility classes (`bg-v2-brand`, `text-v2-neutral-700`, `rounded-v2-md`, …).
 *      DO NOT redeclare V2 colours inside the Tailwind config — change them here
 *      and the utility classes update automatically.
 *   2. Runtime React code that needs a colour as a JS value (canvas, inline SVG
 *      fills, dynamic chart palettes, e-mail templates rendered client-side, etc.)
 *      reads them via `useTheme()` from `@/lib/theme`.
 *
 * Naming:
 *   • `V2_COLORS`   — colour scales (brand, green, neutral, red, gold, bg).
 *   • `V2_RADII`    — border-radius scale (matches the `rounded-v2-*` utilities).
 *   • `V2_FONT`     — font-family stacks (sans / display / mono).
 *   • `V2_SIZES`    — structural sizes (page content max-width, header heights).
 *   • `V2_BREAKPOINTS` — viewport breakpoints (informational; Tailwind defaults are used).
 *
 * Keep this file framework-agnostic — no React imports, no Tailwind imports, no
 * environment-specific code. It must be safely importable from a Next.js config
 * file at build time and from any client/server module at runtime.
 */

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

export const V2_COLORS = {
  /**
   * Brand accent — primary CTA tone, nav active state, marketing chrome strip, footer
   * dividers, page-number bullets in the design system.
   *
   * Per `RentItForward_Web_DesignSystem_v1.html`, brand === green-500 (`#1E8C3A`).
   * The `hover` stop is the canonical green-600 (`#166B2C`). `brand` is kept as a
   * separate alias so components that read `v2-brand` / `bg-v2-brand` continue to work
   * without churning every call-site to `v2-green-500`.
   */
  brand: {
    DEFAULT: "#1E8C3A",
    hover: "#166B2C",
  },
  /**
   * Green scale exactly as published in the canonical design system (line 10-19 of the
   * design HTML). 500 is the primary CTA tone, 600 the hover, 700 the active/pressed.
   * 50–400 are tints used for backgrounds and soft states (badge bg, success surfaces).
   */
  green: {
    50: "#F0FAF2",
    100: "#D4F0DA",
    200: "#A8E0B4",
    300: "#6FCA84",
    400: "#3AAD56",
    500: "#1E8C3A",
    600: "#166B2C",
    700: "#0F4D1F",
    800: "#093314",
    900: "#041C0A",
  },
  /** Warm neutral scale — surfaces, text, borders. Exact match to design HTML. */
  neutral: {
    0: "#FFFFFF",
    50: "#F8F8F6",
    100: "#F0EFEB",
    200: "#E2E0D8",
    300: "#C8C6BC",
    400: "#9B998F",
    500: "#6E6C63",
    600: "#4A4840",
    700: "#2E2D28",
    800: "#1A1916",
    900: "#0E0D0B",
  },
  /** Page background tint — used as the body background on marketing pages. */
  bg: "#F5F4EF",
  /** Destructive accent. 400 is the standard tone, 500 is the deeper "danger" pressed state. */
  red: {
    400: "#E04040",
    500: "#C02828",
  },
  /** Honorific / award accents. Single stop per the design system (`--gold-400`). */
  gold: {
    400: "#C9963A",
  },
} as const;

// ---------------------------------------------------------------------------
// Radii
// ---------------------------------------------------------------------------

export const V2_RADII = {
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  /** Pill-shaped — per design HTML `--radius-full: 999px`. */
  full: "999px",
} as const;

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

export const V2_FONT = {
  sans: [
    "DM Sans",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Roboto",
    "sans-serif",
  ],
  display: ["Sora", "sans-serif"],
  mono: ["DM Mono", "monospace"],
} as const;

// ---------------------------------------------------------------------------
// Structural sizes
// ---------------------------------------------------------------------------

export const V2_SIZES = {
  /** Max content width of the marketing chrome — per design HTML `--max-width: 1200px`. */
  contentMaxWidth: "1200px",
  /** Header height per breakpoint. Design spec canonical value is 72px (`--nav-height`);
   *  we keep the existing responsive ladder (72 / 88 / 108) so the desktop chrome has
   *  room for the larger logo + full nav rail. Mobile == design spec.
   *  If you change any value here, also update LayoutShell's matching `pt-*` rhythm. */
  headerHeight: {
    mobile: "72px",
    sm: "88px",
    lg: "108px",
  },
} as const;

// ---------------------------------------------------------------------------
// Breakpoints (informational — Tailwind defaults are otherwise used)
// ---------------------------------------------------------------------------

export const V2_BREAKPOINTS = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
} as const;

// ---------------------------------------------------------------------------
// Tailwind interop helpers
// ---------------------------------------------------------------------------

/**
 * Tailwind expects colour scales as nested string maps. Our V2_COLORS uses `.brand.DEFAULT`
 * + `.brand.hover` for the brand pair which Tailwind doesn't understand — flatten those
 * into the shape `tailwind.config.ts` consumes. The rest is structurally compatible.
 *
 * The output of this helper is wired into the `v2` namespace of the Tailwind config
 * (`v2.brand`, `v2.green.500`, `v2.neutral.700`, etc.).
 */
export const v2TailwindColors = {
  brand: V2_COLORS.brand.DEFAULT,
  "brand-hover": V2_COLORS.brand.hover,
  green: V2_COLORS.green,
  neutral: V2_COLORS.neutral,
  bg: V2_COLORS.bg,
  red: V2_COLORS.red,
  /**
   * Gold exposed BOTH as a flat utility (`bg-v2-gold`) for back-compat AND as a stop on
   * the gold scale (`bg-v2-gold-400`). The single canonical hex per design system is
   * V2_COLORS.gold[400] — the flat utility resolves to the same value so call-sites that
   * already used `v2-gold` don't break.
   */
  gold: {
    DEFAULT: V2_COLORS.gold[400],
    400: V2_COLORS.gold[400],
  },
} as const;

/**
 * Tailwind interop for the V2 radius scale. Mapped to `rounded-v2-*` utilities.
 */
export const v2TailwindRadii = {
  "v2-sm": V2_RADII.sm,
  "v2-md": V2_RADII.md,
  "v2-lg": V2_RADII.lg,
  "v2-xl": V2_RADII.xl,
  "v2-full": V2_RADII.full,
} as const;

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------

export type V2ColorTokens = typeof V2_COLORS;
export type V2RadiiTokens = typeof V2_RADII;
export type V2FontTokens = typeof V2_FONT;
export type V2SizeTokens = typeof V2_SIZES;
export type V2BreakpointTokens = typeof V2_BREAKPOINTS;

/** The full theme object shape — surfaced via the `useTheme()` hook. */
export interface V2Theme {
  colors: V2ColorTokens;
  radii: V2RadiiTokens;
  font: V2FontTokens;
  sizes: V2SizeTokens;
  breakpoints: V2BreakpointTokens;
}

/** Single immutable theme object — assembled once, frozen, and exposed at runtime. */
export const V2_THEME: V2Theme = Object.freeze({
  colors: V2_COLORS,
  radii: V2_RADII,
  font: V2_FONT,
  sizes: V2_SIZES,
  breakpoints: V2_BREAKPOINTS,
});
